import { useEvent, type ApplicationEvents } from '~/composables/useEventBus';
import { createSheetBackdropState, type SheetBackdropState } from '~/composables/useSheetSwipeBackdrop';

export const useModalsStore = defineStore('ModalsStore', () => {
    const modals = ref<string[]>([]);
    const persistentModals = ref<Set<string>>(new Set());

    const displayOverlay = computed<boolean>(() => modals.value.length > 0);

    // How a mobile sheet's swipe-to-dismiss is fading the overlays drawn behind it. Here
    // because this store already owns whether those overlays show at all; written by
    // `useSheetSwipeBackdrop`, read by `layout/ModalsBackdrop.vue` and `UiActionSheet`.
    const sheetBackdrop = reactive<SheetBackdropState>(createSheetBackdropState());

    const getLatestModal = () => {
        return modals.value[modals.value.length - 1];
    };

    const getModalIndex = (name: string) => {
        return modals.value.findLastIndex((modal: any) => modal === name);
    };

    const getModalZIndex = (name: string) => {
        return 900 + getModalIndex(name) * 2;
    };

    const getClickableOverlayZIndex = () => {
        return getModalZIndex(getLatestModal()) - 1;
    };

    const setPersistent = (name: string, value: boolean) => {
        if (value) {
            persistentModals.value.add(name);
        } else {
            persistentModals.value.delete(name);
        }
    };

    const isPersistent = (name: string) => {
        return persistentModals.value.has(name);
    };

    const closeLatestModal = async () => {
        const name = getLatestModal();

        if (isPersistent(name)) {
            return;
        }

        useEvent((name + ':close') as keyof ApplicationEvents);

        await nextTick();
        if (getModalIndex(name) > -1) {
            useEvent('modal:close', name);
        }
    };

    useListen('modal:open', (name: string) => {
        if (getModalIndex(name) > -1) {
            modals.value.splice(getModalIndex(name), 1);
        }

        // A sheet dismissed by a swipe leaves the overlays faded out on purpose (see
        // `useSheetSwipeBackdrop`); whatever opens next must find them at full strength.
        // Cleared in this same listener so it lands before the overlays render again.
        Object.assign(sheetBackdrop, createSheetBackdropState());

        modals.value.push(name);
    });

    useListen('modal:close', (name: string) => {
        const index = getModalIndex(name);
        if (index > -1) {
            modals.value.splice(index, 1);
        }
    });

    // Prevent body scroll when modals are open
    watch(displayOverlay, (isOpen) => {
        if (isOpen) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
    });

    useHead({
        bodyAttrs: {
            class: computed(() => {
                return displayOverlay.value ? '' : '';
            }),
        },
    });

    return {
        modals,
        persistentModals,
        displayOverlay,
        sheetBackdrop,
        getModalIndex,
        getModalZIndex,
        getClickableOverlayZIndex,
        getLatestModal,
        closeLatestModal,
        setPersistent,
        isPersistent,
    };
});
